import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import TranslateWidget from "@/components/TranslateWidget";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);
  const [cmsContent, setCmsContent] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqRes, contentRes] = await Promise.all([
          axios.get(`${API}/faq/public`),
          axios.get(`${API}/site-content/public`).catch(() => null),
        ]);
        setFaqs(faqRes.data);
        if (contentRes?.data?.logo_url) setLogoUrl(contentRes.data.logo_url);
        setCmsContent(contentRes?.data?.faq_page || {});
      } catch (e) {
        console.error("Failed to fetch FAQ data:", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F0F4F3]" data-testid="faq-page">
      <Helmet>
        <title>FAQ | Commonwealth FC Youth Soccer Charlottesville</title>
        <meta name="description" content="Answers to common questions about Commonwealth FC youth soccer club in Charlottesville, VA — registration, fees, tryouts, age groups, practice schedules, and more." />
        <meta property="og:title" content="FAQ | Commonwealth FC" />
        <meta property="og:description" content="Common questions about Commonwealth FC youth soccer — registration, fees, tryouts, and more." />
        <meta property="og:url" content="https://commonwealthfc.org/faq" />
        <link rel="canonical" href="https://commonwealthfc.org/faq" />
      </Helmet>

      {/* Nav */}
      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-nav-logo">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">
              Commonwealth FC
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#F0F4F3]/80 hover:text-[#C8E64E] text-sm font-medium transition-colors">
              <ArrowLeft size={16} className="inline mr-1" /> Back
            </Link>
            <TranslateWidget />
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-4" data-testid="faq-heading">
            {cmsContent.title || "FREQUENTLY ASKED QUESTIONS"}
          </h1>
          <p className="text-[#6B8A8F] text-base">
            {cmsContent.subtitle || "Everything you need to know about Commonwealth FC"}
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((faq) => (
            <AccordionItem
              key={faq.faq_id}
              value={faq.faq_id}
              className="border border-[#0C2E3C]/10 bg-white px-6 rounded-sm"
              data-testid={`faq-item-${faq.faq_id}`}
            >
              <AccordionTrigger className="font-semibold text-base text-[#0C2E3C] hover:text-[#C8E64E] py-5 hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#6B8A8F] text-base leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {faqs.length === 0 && (
          <p className="text-center text-[#6B8A8F] py-12">No FAQs available yet.</p>
        )}

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-[#6B8A8F] mb-4">Still have questions?</p>
          <Link to="/join">
            <Button className="bg-[#0C2E3C] hover:bg-[#0C2E3C]/90 text-[#F0F4F3] font-bold uppercase tracking-wider text-sm px-8 h-14 rounded-none" data-testid="faq-cta-btn">
              JOIN NOW
            </Button>
          </Link>
        </div>
      </div>

      <footer className="bg-[#0C2E3C] py-6 text-center">
        <p className="text-[#F0F4F3]/40 text-sm">
          &copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default FAQPage;
